
import React, { useState, useEffect } from 'react';
import { KolProfile, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { X, Save, Sparkles } from 'lucide-react';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<KolProfile>) => void;
  profile: KolProfile;
  language: Language;
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ isOpen, onClose, onSave, profile, language }) => {
  const t = TRANSLATIONS[language];
  
  const [formData, setFormData] = useState({
    name: '',
    handle: '',
    bio: '',
    minPrice: 0,
    maxPrice: 0,
    tags: [] as string[]
  });

  const [newTag, setNewTag] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name,
        handle: profile.handle,
        bio: profile.bio,
        minPrice: profile.priceRange.min,
        maxPrice: profile.priceRange.max,
        tags: profile.tags || []
      });
    }
  }, [profile, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      handle: formData.handle,
      bio: formData.bio,
      priceRange: {
        min: Number(formData.minPrice),
        max: Number(formData.maxPrice),
        currency: profile.priceRange.currency
      },
      tags: formData.tags
    });
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTag.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(newTag.trim())) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      }
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  // Simulate AI Analysis
  const handleAiAutoTag = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      // Mock generated tags based on existing profile data
      const generatedTags = ['Web3', 'Growth', 'Influencer', 'Crypto', 'Alpha'];
      setFormData(prev => ({
        ...prev,
        tags: Array.from(new Set([...prev.tags, ...generatedTags]))
      }));
      setIsAnalyzing(false);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-6 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white">
            {t.editProfileTitle}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t.displayName}</label>
            <input 
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t.handle}</label>
            <input 
              type="text"
              required
              value={formData.handle}
              onChange={(e) => setFormData({...formData, handle: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t.bio}</label>
            <textarea 
              rows={3}
              required
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          {/* Tags Section */}
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
            <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-slate-300">{t.tags}</label>
                <button 
                  type="button" 
                  onClick={handleAiAutoTag}
                  disabled={isAnalyzing}
                  className="text-xs flex items-center gap-1 text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50"
                >
                  <Sparkles size={12} />
                  {isAnalyzing ? t.aiTagsAnalyzing : t.aiTags}
                </button>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.tags.map(tag => (
                <span key={tag} className="bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded-md text-xs flex items-center gap-1 border border-indigo-500/30">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-white">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>

            <input 
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleAddTag}
              placeholder={t.tagsPlaceholder}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-1 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
             <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">{t.minPrice}</label>
                <input 
                    type="number"
                    min="0"
                    value={formData.minPrice}
                    onChange={(e) => setFormData({...formData, minPrice: Number(e.target.value)})}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
             </div>
             <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">{t.maxPrice}</label>
                <input 
                    type="number"
                    min="0"
                    value={formData.maxPrice}
                    onChange={(e) => setFormData({...formData, maxPrice: Number(e.target.value)})}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
             </div>
          </div>

          <div className="pt-4 flex gap-3">
             <button 
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
             >
                {t.cancel}
             </button>
             <button 
                type="submit"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
             >
                <Save size={18} /> {t.saveChanges}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};
